/**
 * Admin > Metrics: what the page shows, ported from the design script (admin-usage-metrics.js).
 *
 * Every id here is a series returned by GET /api/admin/metrics/series/ (web/helpers/admin_metrics.py
 * documents how each one is counted), except the derived ones (churn rate, ARPA, LTV), computed in
 * metricsCalc.js from their numerator / denominator. `agg` is how a value covers a period of months:
 * 'sum' adds the months, 'last' is a month-end snapshot, 'rate' / 'arpa' / 'ltv' are the derived formulas.
 * `series` makes an id a label-only alias of another id's series (the same number shown under two names).
 * `hint` is a tooltip shown next to the row label.
 */

function M (id, label, options) {
  return Object.assign({ id, label, fmt: 'int', agg: 'sum', goodDown: false }, options || {})
}
const USD = { fmt: 'usd' }
const SNAP = { agg: 'last' }
const DOWN = { goodDown: true }

function R (id, label, num, den) {
  return { id, label, fmt: 'pct', agg: 'rate', num, den, goodDown: true, derived: true }
}
function A (id, label, rev, den) {
  return { id, label, fmt: 'usd', agg: 'arpa', num: rev, den, goodDown: false, derived: true }
}
function L (id, label, arpa, churn) {
  return { id, label, fmt: 'usd', agg: 'ltv', num: arpa, den: churn, goodDown: false, derived: true }
}

const DEFINITIONS = [
  // POP runs
  M('runs_pro', 'Pro runs'), M('runs_custom', 'Custom runs'), M('reruns', 'Reruns'), M('runs_bulk', 'Bulk run pages'),
  M('api_runs', 'API runs'), M('runs_failed', 'Failed runs', DOWN), M('aio_runs', 'Runs with AI Overview'),
  M('runs_total', 'Total runs'),
  // EEAT / Schema / NLP
  M('eeat_basic', 'Basic EEAT'), M('eeat_next', 'Next EEAT'), M('eeat_total', 'Total EEAT reports'),
  M('schema_runs', 'Schema reports'), M('nlp_runs', 'NLP runs'),
  // AI Writer
  M('aiw_guided', 'Guided articles'), M('aiw_auto', 'Auto articles'), M('aiw_regen', 'Paid regenerations'),
  M('aiw_total', 'Articles (guided + auto)'),
  // Keyword research
  M('kw_silo', '1-Click Silo'), M('kw_custom_silo', 'Custom Silo'), M('kw_insight', 'Keyword Insights'),
  M('kw_strategy_existing', 'Keyword Strategy (existing content)'), M('kw_strategy_new', 'Keyword Strategy (new content)'),
  M('kw_track', 'Keyword Tracking set up'), M('kw_failed', 'Failed research', DOWN),
  M('kw_total', 'Total keyword reports'),
  // Watchdog
  M('wd_sent', 'Reports sent'), M('wd_fail', 'Failed reports', DOWN), M('wd_setup', 'New setups'),
  M('wd_pages', 'Active tracked pages', SNAP),
  // AI Visibility
  M('aiv_new', 'Trackers created'), M('aiv_runs', 'Refresh runs'), M('aiv_failed', 'Failed runs', DOWN),
  M('aiv_credits', 'Credits used', { series: 'cr_aiv' }),
  // Review optimizations
  M('ro_paid', 'Orders paid'), M('ro_done', 'Reviews completed'), M('ro_credits', 'Credits used', { series: 'cr_ro' }),
  // White Glove
  M('wg_users', 'Active WG users', SNAP), M('wg_cancel', 'WG cancellations', DOWN),
  R('wg_churn_rate', 'Churn rate', 'wg_cancel', 'wg_users'), A('wg_arpa', 'ARPA (monthly)', 'wg_rev', 'wg_users'),
  L('wg_ltv', 'LTV', 'wg_arpa', 'wg_churn_rate'),
  M('wg_audit', 'Audit orders'), M('wg_silo', 'Silo orders'), M('wg_content', 'Content orders'),
  M('wg_topups', 'Balance top-ups'), M('wg_rev', 'WG revenue', USD),
  // POP Authority
  M('pa_active', 'Active subscriptions', SNAP), M('pa_new', 'New subscriptions (first paid)'),
  M('pa_setup', 'Setup fees paid'), M('pa_audits', 'Audits delivered'), M('pa_pkg_inv', 'Package invoices paid'),
  M('pa_boost', 'Booster orders'), M('pa_content', 'Content only orders'), M('pa_topups', 'Balance top-ups'),
  M('pa_articles', 'Articles delivered'), M('pa_churn', 'Subscriptions ended', DOWN),
  R('pa_churn_rate', 'Churn rate', 'pa_churn', 'pa_active'), A('pa_arpa', 'ARPA (monthly)', 'pa_rev', 'pa_active'),
  L('pa_ltv', 'LTV', 'pa_arpa', 'pa_churn_rate'),
  M('pa_rev', 'Authority revenue', USD), M('pa_comm', 'CM commissions', USD),
  // API packages
  M('api_keys', 'Active API keys', SNAP), M('api_rev', 'API revenue', USD, { series: 'rev_api' }),
  // Users by package
  M('pkg_basic', 'Basic', SNAP), M('pkg_premium', 'Premium (legacy)', SNAP), M('pkg_unl', 'Unlimited (monthly)', SNAP),
  M('pkg_unl_y', 'Unlimited (yearly)', SNAP), M('pkg_teams', 'Teams (monthly)', SNAP), M('pkg_teams_y', 'Teams (yearly)', SNAP),
  M('pkg_wg', 'White Glove', SNAP), M('pkg_pa_starter', 'Authority Starter', SNAP), M('pkg_pa_growth', 'Authority Growth', SNAP),
  M('pkg_pa_pro', 'Authority Pro', SNAP), M('pkg_pa_custom', 'Authority Custom', SNAP), M('pkg_wd', 'Watchdog only', SNAP),
  M('pkg_sub', 'Sub-accounts (seats in use)', SNAP), M('pkg_trial', 'Trial', SNAP), M('pkg_credits', 'Credits only', SNAP),
  M('pkg_total', 'Total paid accounts', SNAP),
  // Signups & churn
  M('su_new', 'New signups (verified)'), M('su_cancel', 'Cancellations', DOWN), M('su_paid', 'New paying customers', {
    hint: 'Accounts that joined in the month and pay a monthly amount today, as in All users filtered on '
      + 'Joined and Pays per month. A past month drops as its signups cancel.',
  }),
  R('churn_rate', 'Churn rate', 'su_cancel', 'pkg_total'), A('arpa', 'ARPA (monthly)', 'rev_total', 'pkg_total'),
  L('ltv', 'LTV', 'arpa', 'churn_rate'),
  // Revenue
  M('rev_subs', 'Subscriptions', USD), M('rev_credits', 'Credit purchases', USD), M('rev_refill', 'Auto-refill charges', USD),
  M('rev_wg', 'White Glove orders', USD), M('rev_topup', 'Balance top-ups', USD), M('rev_pa_pkg', 'Authority packages', USD),
  M('rev_pa_setup', 'Authority setup fees', USD), M('rev_pa_boost', 'Authority boosters', USD),
  M('rev_api', 'API packages', USD),
  M('rev_other', 'Other', USD, {
    hint: 'Paid invoices that match no product rule: no plan, seat, credit pack, API package, top-up, '
      + 'POP Authority or White Glove product or label (for example legacy invoices with a generic label).',
  }),
  M('rev_total', 'Total revenue', USD),
  // Credits
  M('cr_bought', 'Purchased'), M('cr_used', 'Consumed'), M('cr_coupon', 'Granted via coupons'),
  M('cr_balance', 'Outstanding balance', SNAP, {
    hint: 'Today only: the credits all accounts hold right now, plan allowance (resets each cycle) plus '
      + 'purchased balance (never expires).',
  }),
  M('cr_report', 'Consumed: POP reports'),
  M('cr_eeat', 'Consumed: EEAT'), M('cr_nlp', 'Consumed: NLP'), M('cr_aio', 'Consumed: AI Overview'),
  M('cr_wd', 'Consumed: Watchdog'), M('cr_ai', 'Consumed: AI articles'), M('cr_schema', 'Consumed: Schema'),
  M('cr_kw', 'Consumed: Keyword research'), M('cr_aiv', 'Consumed: AI Visibility'), M('cr_rocky', 'Consumed: Rocky'),
  M('cr_ro', 'Consumed: Review optimizations'), M('cr_bulk', 'Consumed: Bulk runs'),
  // Coupons
  M('cp_new', 'Coupons created', { hint: 'Coupons created by hand in Admin > Coupons, except those given to an affiliate.' }),
  M('cp_system', 'System coupons', {
    hint: 'Coupons POP generates on its own, one per user: the abandoned-registration offer and the inactivity '
      + 'win-back (10% off, hidden from the admin coupon list).',
  }),
  M('cp_affiliate', 'Affiliate coupons', { hint: 'Coupons given to an affiliate to share with the people they refer.' }),
  M('cp_redeem', 'Redemptions'), M('cp_aff_redeem', 'Affiliate code redemptions'), M('cp_credits', 'Credits granted'),
  // Auto-refills
  M('rf_active', 'Active refill plans', SNAP), M('rf_charges', 'Refill charges', USD), M('rf_credits', 'Credits refilled'),
  // Affiliates
  M('af_sales', 'Attributed sales', USD), M('af_comm', 'Commissions accrued', USD), M('af_paid', 'Payouts paid', USD),
  M('af_active', 'Active affiliates', SNAP),
]

export const METRICS = {}
DEFINITIONS.forEach((def) => { METRICS[def.id] = def })

/** The sections and their product cards: `head` is the card's headline metric, `total` the table's last row. */
export const SECTIONS = [
  {
    id: 'usage',
    name: 'Usage by product',
    products: [
      { id: 'pop_runs', name: 'POP Runs', head: 'runs_total',
        note: 'Feeds the new POP Runs page (not built yet). Custom vs Pro split comes from run type.',
        metrics: ['runs_pro', 'runs_custom', 'reruns', 'runs_bulk', 'api_runs', 'aio_runs', 'runs_failed'], total: 'runs_total' },
      { id: 'eeat', name: 'EEAT Reports', head: 'eeat_total', metrics: ['eeat_basic', 'eeat_next'], total: 'eeat_total' },
      { id: 'schema', name: 'Schema Reports', head: 'schema_runs', metrics: ['schema_runs'] },
      { id: 'nlp', name: 'Google NLP', head: 'nlp_runs', metrics: ['nlp_runs'] },
      { id: 'aiw', name: 'AI Writer', head: 'aiw_total',
        metrics: ['aiw_guided', 'aiw_auto', 'aiw_regen'], total: 'aiw_total' },
      { id: 'kw', name: 'Keyword Research', head: 'kw_total',
        metrics: ['kw_silo', 'kw_custom_silo', 'kw_insight', 'kw_strategy_existing', 'kw_strategy_new', 'kw_track',
          'kw_failed'],
        total: 'kw_total' },
      { id: 'wd', name: 'Watchdog', head: 'wd_sent', metrics: ['wd_sent', 'wd_fail', 'wd_setup', 'wd_pages'] },
      { id: 'aiv', name: 'AI Visibility', head: 'aiv_runs', metrics: ['aiv_new', 'aiv_runs', 'aiv_failed', 'aiv_credits'] },
      { id: 'rev_opt', name: 'Review Optimizations', head: 'ro_done', metrics: ['ro_paid', 'ro_done', 'ro_credits'] },
      { id: 'wg', name: 'White Glove', head: 'wg_users', cm: true, funnel: true,
        metrics: ['wg_users', 'wg_cancel', 'wg_churn_rate', 'wg_arpa', 'wg_ltv', 'wg_audit', 'wg_silo', 'wg_content',
          'wg_topups', 'wg_rev'] },
      { id: 'pa', name: 'POP Authority', head: 'pa_active', cm: true,
        note: 'Source: POP Authority admin (setup invoices, package invoices, boosters, content only orders).',
        metrics: ['pa_active', 'pa_new', 'pa_setup', 'pa_audits', 'pa_pkg_inv', 'pa_boost', 'pa_content', 'pa_topups',
          'pa_articles', 'pa_churn', 'pa_churn_rate', 'pa_arpa', 'pa_ltv', 'pa_rev', 'pa_comm'] },
      { id: 'api', name: 'API Packages', head: 'api_runs', metrics: ['api_runs', 'api_keys', 'api_rev'] },
    ],
  },
  {
    id: 'users',
    name: 'Users & accounts',
    products: [
      { id: 'pkg', name: 'Users by Package', head: 'pkg_total',
        metrics: ['pkg_basic', 'pkg_premium', 'pkg_unl', 'pkg_unl_y', 'pkg_teams', 'pkg_teams_y', 'pkg_wg', 'pkg_pa_starter',
          'pkg_pa_growth', 'pkg_pa_pro', 'pkg_pa_custom', 'pkg_wd', 'pkg_sub', 'pkg_trial', 'pkg_credits'],
        total: 'pkg_total' },
      { id: 'flow', name: 'Signups & Churn', head: 'su_new',
        note: 'Churn rate = cancellations in the period / paid accounts at the start of the period (changes in percentage points). ARPA = revenue / average paid accounts, per month. LTV = ARPA / monthly churn rate.',
        metrics: ['su_new', 'su_cancel', 'su_paid', 'churn_rate', 'arpa', 'ltv'] },
    ],
  },
  {
    id: 'money',
    name: 'Revenue & credits',
    products: [
      { id: 'revenue', name: 'Revenue by Product', head: 'rev_total',
        metrics: ['rev_subs', 'rev_credits', 'rev_refill', 'rev_wg', 'rev_topup', 'rev_pa_pkg', 'rev_pa_setup', 'rev_pa_boost',
          'rev_api', 'rev_other'], total: 'rev_total' },
      { id: 'credits', name: 'Credits', head: 'cr_used',
        metrics: ['cr_bought', 'cr_used', 'cr_coupon', 'cr_balance', 'cr_report', 'cr_eeat', 'cr_nlp', 'cr_aio', 'cr_wd', 'cr_ai',
          'cr_schema', 'cr_kw', 'cr_aiv', 'cr_rocky', 'cr_ro', 'cr_bulk'] },
      { id: 'coupons', name: 'Coupons', head: 'cp_redeem',
        metrics: ['cp_new', 'cp_system', 'cp_affiliate', 'cp_redeem', 'cp_aff_redeem', 'cp_credits'] },
      { id: 'refill', name: 'Credit Auto-refills', head: 'rf_active', metrics: ['rf_active', 'rf_charges', 'rf_credits'] },
      { id: 'aff', name: 'Affiliates', head: 'af_sales', metrics: ['af_sales', 'af_comm', 'af_paid', 'af_active'] },
    ],
  },
]

/** The KPI strip. */
export const KPIS = [
  { id: 'rev_total', label: 'Total revenue' },
  { id: 'pkg_total', label: 'Paid accounts' },
  { id: 'runs_total', label: 'POP runs' },
  { id: 'cr_used', label: 'Credits consumed' },
  { id: 'su_new', label: 'New signups' },
  { id: 'churn_rate', label: 'Churn rate' },
  { id: 'ltv', label: 'LTV' },
]

export function productById (id) {
  for (const section of SECTIONS) {
    for (const product of section.products) if (product.id === id) return product
  }
  return null
}
